import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
//components
import Container from "../../../components/container/container";
import Card from "../../../components/cards/cards";
import Button from "../../../components/buttons/buttons";
import CountdownTimer from "../../../components/countdownTimer";
import NavBarCommon from "../../../components/navigation/NavBarCommon";
//images and icons
// import Banner from "../../../assets/images/pictures/track-banner.jpg";
import Banner from "../../../assets/images/pictures/track-order-bw.webp";
import LEFT from "../../../assets/images/icons/arrow-left.svg";
import {
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  TruckIcon,
  UserIcon,
  CheckIcon,
  BadgeCheckIcon,
} from "@heroicons/react/outline";
import { ContactModal } from "../../../components/modals/contactModal";
import { RestaurantInfo } from "../../../components/modals/restaurantInfo";
import {
  getARestaurantOrderAction,
  trackRestaurantOrderAction,
} from "../../../redux/restaurantOrder/restaurantOrderActions";
import {
  getSingleStoreOrder,
  trackStoreOrderAction,
} from "../../../redux/storeOrders/storeOrderAction";

const tabs = [
  { name: "Order Status", href: "status" },
  { name: "Order Details", href: "details" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const STATUS_STEPS = [
  { key: "shipped", label: "Order Shipped", Icon: TruckIcon },
  { key: "assigned", label: "Order Assigned", Icon: UserIcon },
  { key: "accepted", label: "Order Accepted", Icon: CheckIcon },
  { key: "delivered", label: "Order Delivered", Icon: BadgeCheckIcon },
];

function OrderRoadmap({ status }) {
  // const currentIndex = STATUS_STEPS.findIndex((s) => s.key === status);
  // const completedIndex = currentIndex === -1 ? STATUS_STEPS.length - 1 : currentIndex;

  return (
    <div className="flex flex-col gap-0 py-4">
      {STATUS_STEPS.map((step, i) => {
        const { Icon } = step;
        const isLast = i === STATUS_STEPS.length - 1;
        return (
          <div key={step.key} className="flex items-start gap-4">
            {/* Line + icon column */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-[#FFEAE5] flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              {!isLast && (
                <div className="w-0.5 h-14 bg-[#EEEEEE]" />
              )}
            </div>
            {/* Label */}
            <p className="text-sm font-medium mt-3 text-gray-700 dark:text-[#BCBCBC]">
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Tracking page for a restaurant or store order.
 * @param {"restaurant" | "store"} type
 */
export default function TrackOrder({ type = "restaurant" }) {
  const DEFAULT_ACTIVE_TAB = "status";
  const isStore = type === "store";
  const { active_tab, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order: restaurantOrder } = useSelector(
    (state) => state.restaurantOrder
  );
  const { singleStoreOrder } = useSelector((state) => state.storeOrder);
  const order = isStore ? singleStoreOrder : restaurantOrder;
  const basePath = isStore ? `/track/store/${id}` : `/track/${id}`;

  let [isOpen, setIsOpen] = useState(false);
  const [trackCourier, setTrackCourier] = useState({});

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }
  let [isVisible, setIsVisible] = useState(false);

  function close() {
    setIsVisible(false);
  }

  function open() {
    setIsVisible(true);
  }

  const trackOrder = async () => {
    const trackAction = isStore
      ? trackStoreOrderAction
      : trackRestaurantOrderAction;
    const res = await dispatch(trackAction({ id: order.id }));
    // The action shows its own error and returns nothing when the request fails
    setTrackCourier(res?.data?.data || {});
  };

  useEffect(() => {
    if (!active_tab) {
      navigate(`${basePath}/${DEFAULT_ACTIVE_TAB}`);
    }
  });

  useEffect(() => {
    if (id) {
      dispatch(isStore ? getSingleStoreOrder(id) : getARestaurantOrderAction(id));
    }
    // eslint-disable-next-line
  }, [dispatch, id, isStore]);

  useEffect(() => {
    // The id check skips an order left in redux from a previous visit
    if (String(order?.id) === String(id) && order?.status === "shipped") {
      trackOrder();
    }
    // eslint-disable-next-line
  }, [dispatch, order]);

  const toggle = (tab) => {
    if (active_tab !== tab) {
      navigate(`${basePath}/${tab}`);
    }
  };

  const orderStatus =
    trackCourier?.courier_orders?.length > 0
      ? trackCourier?.courier_orders?.[0]?.status
      : order?.status;
  const isCompleted =
    orderStatus === "delivered" || orderStatus === "completed";

  return (
    <div data-test="trackOrderComponent">
      <NavBarCommon />
      <div
        className="min-h-96 bg-cover no-repeat bg-center relative z-0"
        style={{
          background: `linear-gradient(45deg, rgb(0 0 0 / 90%), rgba(255, 255, 255, 0)), url('${Banner}')`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Container>
          <div className="py-16">
            <button
              type="button"
              className="text-primary font-bold flex items-center gap-2"
              onClick={() =>
                navigate("/profile/orders", {
                  state: { tab: isStore ? "store" : "restaurant" },
                })
              }
            >
              <img src={LEFT} alt="close" className=" w-3 h-3 sm:w-4 sm:h-4" />
              <span>Back to My Orders</span>
            </button>
            <h2
              className="text-5xl font-bold mb-5 mt-10 capitalize text-white"
              style={{ lineHeight: "64px" }}
            >
              {isStore
                ? order?.store?.store_name
                : order?.restaurant?.restaurant_name}
            </h2>
            <p className="text-xl text-white">Your order details</p>
          </div>
        </Container>
      </div>

      <Container>
        <main className="py-8">
          <div className="max-w-3xl mx-auto lg:max-w-7xl">
            <h1 className="sr-only">Page title</h1>
            {/* Main 3 column grid */}
            <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-16">
              {/* Left column */}
              <div className="grid grid-cols-1 gap-4 lg:col-span-2">
                <section aria-labelledby="section-1-title">
                  {/* Your content */}
                  <div>
                    <div className="border-b border-gray-200 dark:border-none">
                      <nav
                        className="-mb-px flex space-x-8"
                        aria-label="Tabs"
                      >
                        {tabs.map((tab) => (
                          <button
                            key={tab.name}
                            onClick={() => {
                              toggle(tab.href);
                            }}
                            className={classNames(
                              tab.href === active_tab
                                ? "border-[#333333] dark:border-primary text-[#333333] dark:text-primary font-bold"
                                : "border-transparent text-gray-500 dark:text-[#BCBCBC] hover:text-gray-700 hover:border-gray-300",
                              "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                            )}
                            aria-current={
                              tab.href === active_tab ? "page" : undefined
                            }
                          >
                            {tab.name}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                  <div className="mt-8">
                    {active_tab === "status" && (
                      <Card.OrderStatus order={order} track={trackCourier} />
                    )}
                    {active_tab === "details" && (
                      <Card.OrderDetails order={order} />
                    )}
                  </div>
                </section>
              </div>

              {/* Right column */}
              <div className="grid grid-cols-1 gap-4">
                <section aria-labelledby="section-2-title">
                  <h2 className="sr-only" id="section-2-title">
                    Order Progress
                  </h2>

                  <div className="p-2 md:p-6">
                    {/* Your content */}
                    {isCompleted ? (
                      <OrderRoadmap status={order?.status} />
                    ) : (
                      <CountdownTimer order={order} track={trackCourier} />
                    )}

                    <div className="mt-16 mb-4 text-center">
                      <p className="mb-4">You haven’t received your order yet? </p>
                      <Button.Primary
                        title={'Contact Support'}
                        onClick={openModal}
                        className="px-4 rounded-lg w-full"
                        type={'button'}
                      />
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <div className="mt-20">
            <p className="py-3">Need help with your order?</p>
            <div className="border-b-2 dark:border-[#505050] max-w-[636px] mb-8"></div>
            <div className="flex flex-col gap-8">
              <div className="flex gap-4 cursor-pointer" onClick={openModal}>
                <div className="w-[48px] h-[48px] bg-[#FFEAE5] rounded items-center justify-center flex">
                  <QuestionMarkCircleIcon
                    className="h-6 w-6 text-primary"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h6>Contact Support</h6>
                  <p>If you need help with your order</p>
                </div>
              </div>
              <ContactModal show={isOpen} onClose={closeModal} />

              {!isStore && (
                <>
                  <div className="flex gap-4 cursor-pointer" onClick={open}>
                    <div className="w-[48px] h-[48px] bg-[#FFEAE5] rounded items-center justify-center flex">
                      <InformationCircleIcon
                        className="h-6 w-6 text-primary"
                        aria-hidden="true"
                      />
                    </div>
                    <div>
                      <h6>Restaurant Details</h6>
                      <p>Opening hours and contact details</p>
                    </div>
                  </div>
                  <RestaurantInfo show={isVisible} onClose={close} />
                </>
              )}
            </div>
          </div>
        </main>
      </Container>
    </div>
  );
}
